import { FC, useMemo } from "react";
import { LoanApplication, LoanPaymentStatus } from "../../../contexts";
import { formatAmount } from "../../../utils";
import { useLoanApplicationPayments } from "../../../hooks";
import { useLoanApplicationPaymentsEnabled } from "../../../hooks/useLoanApplicationPaymentsEnabled";

type LoanDrawdownRequestsTableProps = {
  loan: LoanApplication;
};

export const LoanDrawdownRequestsTable: FC<LoanDrawdownRequestsTableProps> = ({
  loan,
}) => {
  const loanPaymentsEnabled = useLoanApplicationPaymentsEnabled(loan);

  const { data: loanPayments = [] } = useLoanApplicationPayments(
    loan.id,
    loanPaymentsEnabled
  );

  const paidRequests = useMemo(() => {
    return loanPayments.filter(
      (payment) => payment.status === LoanPaymentStatus.PAID
    );
  }, [loanPayments]);

  const otherRequests = useMemo(() => {
    return loanPayments.filter(
      (payment) => payment.status !== LoanPaymentStatus.PAID
    );
  }, [loanPayments]);

  return (
    <div>
      <div className="cx-text-text-primary cx-font-semibold cx-text-lg cx-mb-4">
        Drawdown Requests
      </div>

      <div className="cx-w-full cx-flex cx-flex-col cx-gap-y-2 cx-rounded-xl cx-border-stroke-primary cx-border cx-p-2">
        <div className="cx-flex cx-flex-row cx-items-center cx-p-2 cx-min-h-[40px]">
          <div className="cx-w-1/6 cx-text-text-secondary cx-font-medium cx-text-base">
            Drawdown
          </div>
          <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium cx-text-base">
            Amount
          </div>
          <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium cx-text-base">
            Due date
          </div>
          <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium cx-text-base">
            Amount due
          </div>
          <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium cx-text-base">
            Status
          </div>
        </div>

        {otherRequests.map((payment, index) => (
          <div
            key={`drawdown-request-${index}`}
            className={`cx-flex cx-flex-row cx-items-center cx-p-2 cx-min-h-[40px] cx-rounded-lg ${
              index % 2 === 0
                ? "cx-bg-background-secondary"
                : "cx-bg-background-default"
            }`}
          >
            <div className="cx-w-1/6 cx-text-text-secondary cx-font-medium">
              {payment.installmentId}
            </div>
            <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium">
              <div className="cx-font-aed-font"> D </div>{" "}
              {formatAmount(payment.amount)}
            </div>
            <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium">
              {new Date(payment.date).toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </div>
            <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium">
              <div className="cx-font-aed-font"> D </div>{" "}
              {formatAmount(payment.amount)}
            </div>
            <div className="cx-w-1/5">
              <div
                className={`cx-inline-block cx-px-4 cx-py-1 cx-rounded-2xl ${getStatusColorClass(
                  payment.status
                )}`}
              >
                {getStatusDisplay(payment.status)}
              </div>
              {payment.status === "EXCESS_PAYMENT" &&
                payment.exceededAmount && (
                  <div className="cx-mt-1 cx-px-2 cx-text-sm cx-text-[#6F42C1]">
                    <div className="cx-font-aed-font"> D </div>{" "}
                    {formatAmount(payment.exceededAmount)}
                  </div>
                )}
            </div>
          </div>
        ))}

        {paidRequests.length > 0 && (
          <>
            <div className="cx-w-full cx-flex cx-flex-row cx-items-center cx-py-2 cx-min-h-[47px] cx-relative">
              <div className="cx-w-full cx-h-[2px] cx-rounded cx-bg-gradient-to-br cx-from-brand-secondary-regular cx-to-brand-primary-regular" />
              <div className="cx-flex cx-items-center cx-justify-center cx-absolute cx-top-0 cx-left-0 cx-bottom-0 cx-right-0">
                <div className="cx-bg-background-default">
                  <div className="cx-px-4 cx-py-2 cx-text-transparent cx-text-xs cx-font-medium cx-uppercase cx-bg-clip-text cx-bg-gradient-to-br cx-from-brand-secondary-regular cx-to-brand-primary-regular">
                    Paid requests
                  </div>
                </div>
              </div>
            </div>

            {paidRequests.map((payment, index) => (
              <div
                key={`paid-request-${index}`}
                className={`cx-flex cx-flex-row cx-items-center cx-p-2 cx-min-h-[40px] cx-rounded-lg ${
                  index % 2 === 0
                    ? "cx-bg-background-secondary"
                    : "cx-bg-background-default"
                }`}
              >
                <div className="cx-w-1/6 cx-text-text-secondary cx-font-medium">
                  {payment.installmentId}
                </div>
                <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium">
                  <div className="cx-font-aed-font"> D </div>{" "}
                  {formatAmount(payment.amount)}
                </div>
                <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium">
                  {new Date(payment.date).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
                <div className="cx-w-1/5 cx-text-text-secondary cx-font-medium">
                  <div className="cx-font-aed-font"> D </div>{" "}
                  {formatAmount(payment.amount)}
                </div>
                <div className="cx-w-1/5">
                  <div className="cx-inline-block cx-px-2 cx-py-1 cx-rounded-2xl cx-bg-green-100 cx-text-green-700">
                    Paid
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

// Helper functions to get status display and color classes
function getStatusDisplay(status: string): string {
  switch (status) {
    case "SUBMITTED":
      return "Submitted";
    case "IN_REVIEW_OPS":
      return "In Review (Ops)";
    case "IN_REVIEW_SENIOR_OPS":
      return "In Review (Senior Ops)";
    case "ACTIVE":
      return "Active";
    case "OVERPAID":
      return "Active";
    case "ADDITIONAL_INFO_REQUIRED":
      return "Action Required";
    case "WAITING_DISBURSAL":
      return "Awaiting Disbursment";
    case "SCHEDULED":
      return "Scheduled";
    case "DUE":
      return "Due";
    case "OVERDUE":
      return "Overdue";
    case "FORCED_CLOSURE":
      return "Closed";
    case "EARLY_CLOSED":
      return "Closed";
    case "CLOSED_WRITTEN_OFF":
      return "Closed";
    case "CLOSED_OBLIGATIONS_MET":
      return "Closed";
    case "PAID":
      return "Paid";
    case "DECLINED_OPS":
      return "Rejected";
    case "PARTIALLY_PAID":
      return "Partially paid";
    case "LATE_FEE_APPLIED":
      return "Late Fee Applied";
    case "REJECTED_BY_OPS":
      return "Rejected by Ops";
    case "EXCESS_PAYMENT":
      return "Excess payment";
    default:
      return status;
  }
}

function getStatusColorClass(status: string): string {
  switch (status) {
    case "SUBMITTED":
      return "cx-bg-[#FFF3E0] cx-text-[#F57C00]";
    case "IN_REVIEW_OPS":
      return "cx-bg-[#E8F5E9] cx-text-[#388E3C]";
    case "IN_REVIEW_SENIOR_OPS":
      return "cx-bg-[#E8F5E9] cx-text-[#388E3C]";
    case "ACTIVE":
      return "cx-bg-[#E8F5E9] cx-text-[#388E3C]";
    case "OVERPAID":
      return "cx-bg-[#E8F5E9] cx-text-[#388E3C]";
    case "ADDITIONAL_INFO_REQUIRED":
      return "cx-bg-[#FFF1F1] cx-text-[#DA1E28]";
    case "WAITING_DISBURSAL":
      return "cx-bg-[#E8FFED] cx-text-[#108723]";
    case "SCHEDULED":
      return "cx-bg-[#E4FFF7] cx-text-[#20C997]";
    case "DUE":
      return "cx-bg-yellow-100 cx-text-yellow-500";
    case "OVERDUE":
      return "cx-bg-[#FFF1F1] cx-text-[#EB6200]";
    case "FORCED_CLOSURE":
      return "cx-bg-[#EEEEEE] cx-text-[#666666]";
    case "EARLY_CLOSED":
      return "cx-bg-[#EEEEEE] cx-text-[#666666]";
    case "CLOSED_WRITTEN_OFF":
      return "cx-bg-[#EEEEEE] cx-text-[#666666]";
    case "CLOSED_OBLIGATIONS_MET":
      return "cx-bg-[#EEEEEE] cx-text-[#666666]";
    case "DECLINED_OPS":
      return "cx-text-[#FF4500] cx-bg-[#FF45001A]";
    case "PAID":
      return "cx-bg-green-100 cx-text-green-700";
    case "PARTIALLY_PAID":
      return "cx-bg-blue-100 cx-text-blue-700";
    case "LATE_FEE_APPLIED":
      return "cx-text-[#FF4500] cx-bg-[#FF45001A]";
    case "EXCESS_PAYMENT":
      return "cx-bg-[#F6F0FF] cx-text-[#6F42C1]";
    default:
      return "cx-bg-gray-100 cx-text-gray-700";
  }
}

export default LoanDrawdownRequestsTable;
